import type { DomainEvent, InvoiceCreatedEvent } from "@invoicing-sdk/domain";

/**
 * Event handler function type
 */
export type EventHandler<T extends DomainEvent = DomainEvent> = (
  event: T
) => Promise<void>;

/**
 * Event subscription interface
 */
export interface EventSubscription<T extends DomainEvent = DomainEvent> {
  readonly eventType: T["eventType"];
  readonly handler: EventHandler<T>;
  unsubscribe(): Promise<void>;
}

/**
 * Event bus interface for publishing and subscribing to domain events
 */
export interface EventBus {
  /**
   * Publish a domain event to all subscribers
   */
  publish<T extends DomainEvent>(event: T): Promise<void>;

  /**
   * Subscribe to a specific event type
   * - eventType is now strictly typed to the discriminant of T
   * - the returned subscription is also typed with T
   */
  subscribe<T extends DomainEvent>(
    eventType: T["eventType"],
    handler: EventHandler<T>
  ): Promise<EventSubscription<T>>;

  /**
   * Start the event bus (connect to message broker)
   */
  start(): Promise<void>;

  /**
   * Stop the event bus (disconnect from message broker)
   */
  stop(): Promise<void>;

  /**
   * Check if the event bus is connected
   */
  isConnected(): Promise<boolean>;
}

/**
 * Event bus configuration interface
 */
export interface EventBusConfig {
  readonly connectionString: string;
  readonly exchangeName?: string;
  readonly queuePrefix?: string;
  readonly retryAttempts?: number;
  readonly retryDelay?: number;
}

/**
 * Base event bus error
 */
export class EventBusError extends Error {
  constructor(message: string, public override readonly cause?: Error) {
    super(message);
    this.name = "EventBusError";
  }
}

/**
 * Event publishing error
 */
export class EventPublishError extends EventBusError {
  constructor(eventType: string, cause?: Error) {
    super(`Failed to publish event: ${eventType}`, cause);
    this.name = "EventPublishError";
  }
}

/**
 * Event subscription error
 */
export class EventSubscriptionError extends EventBusError {
  constructor(eventType: string, cause?: Error) {
    super(`Failed to subscribe to event: ${eventType}`, cause);
    this.name = "EventSubscriptionError";
  }
}
